import React from "react";
import { motion } from "motion/react";
import {
  User,
  Mail,
  Phone,
  Shield,
  Edit3,
  Trash2,
  Calendar,
  Eye,
  CheckCircle,
  XCircle,
} from "lucide-react";

const AdminCard = ({
  admin,
  onEdit,
  onDelete,
  onViewPermissions,
  onToggleStatus,
}) => {
  // Calculate total permissions from the grouped permissions object
  const getTotalPermissions = (permissions) => {
    if (!permissions || typeof permissions !== "object") return 0;
    return Object.values(permissions).reduce((total, modulePermissions) => {
      return (
        total +
        (Array.isArray(modulePermissions) ? modulePermissions.length : 0)
      );
    }, 0);
  };

  // Get permissions summary for display
  const getPermissionsSummary = (permissions) => {
    if (!permissions || typeof permissions !== "object") return [];

    const summary = [];
    Object.entries(permissions).forEach(([module, modulePermissions]) => {
      if (Array.isArray(modulePermissions) && modulePermissions.length > 0) {
        summary.push({
          module,
          count: modulePermissions.length,
          sample: modulePermissions[0], // Show first permission as sample
        });
      }
    });
    return summary.slice(0, 3); // Show only first 3 modules
  };

  const totalPermissions = getTotalPermissions(admin.permissions);
  const permissionsSummary = getPermissionsSummary(admin.permissions);
  const isActive =
    admin.status === true || admin.status === 1 || admin.status === "1" || admin.status === "active"; 

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300"
    >
      {/* Card Header */}
      <div className="flex-shrink-0 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
        <div className="flex justify-between items-start">
          <div className="flex flex-1 gap-3 items-center min-w-0">
            <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {admin.first_name} {admin.last_name}
              </h3>
              <p className="text-xs font-medium text-blue-600">
                ID: {admin.admin_slug}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-1">
            <button
              onClick={() => onViewPermissions(admin)}
              className="p-1.5 text-gray-500 rounded-md hover:text-blue-600 hover:bg-blue-100 transition-colors"
              title="View Permissions"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(admin)}
              className="p-1.5 text-gray-500 rounded-md hover:text-blue-600 hover:bg-blue-100 transition-colors"
              title="Edit Admin"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(admin)}
              className="p-1.5 text-gray-500 rounded-md hover:text-red-600 hover:bg-red-100 transition-colors"
              title="Delete Admin"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="flex flex-1 flex-col min-h-0 p-4">
        <div className="flex-1 min-h-0">
        {/* Contact Information */}
        <div className="space-y-2 mb-4">
          <div className="flex gap-2 items-center">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">{admin.email}</span>
          </div>
          {admin.phone_number && (
            <div className="flex gap-2 items-center">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {admin.phone_number}
              </span>
            </div>
          )}
        </div>

        {/* Role and Status */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2 items-center">
            <Shield className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {admin.role || "Administrator"}
            </span>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full shadow-sm ring-1 ring-black/5 ${
              isActive
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : "bg-amber-50 text-amber-700 ring-amber-200"
            }`}
          >
            {isActive ? (
              <><CheckCircle className="w-3.5 h-3.5" /> Active</>
            ) : (
              <><XCircle className="w-3.5 h-3.5" /> Inactive</>
            )}
          </span>
        </div>

        {/* Permissions Summary */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-700">Permissions</h4>
            <span className="text-xs text-gray-500">
              {totalPermissions} total
            </span>
          </div>
          <div className="space-y-1">
            {permissionsSummary.map((perm, index) => (
              <div
                key={index}
                className="flex justify-between items-center text-xs"
              >
                <span className="text-gray-600 font-medium">{perm.module}</span>
                <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {perm.count} permissions
                </span>
              </div>
            ))}
            {totalPermissions > 0 && permissionsSummary.length === 0 && (
              <span className="text-xs text-gray-500">Permissions loaded</span>
            )}
            {totalPermissions === 0 && (
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                No permissions
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onToggleStatus(admin)}
            className={`flex-1 flex gap-2 items-center justify-center px-3 py-2.5 text-xs font-semibold rounded-xl transition-all shadow-sm ${
              isActive
                ? "text-red-700 bg-red-50 hover:bg-red-100 border border-red-200/80 hover:border-red-300"
                : "text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80 hover:border-emerald-300"
            }`}
          >
            {isActive ? (
              <>
                <XCircle className="w-3.5 h-3.5" />
                Disapprove
              </>
            ) : (
              <>
                <CheckCircle className="w-3.5 h-3.5" />
                Approve
              </>
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onViewPermissions(admin)}
            className="flex gap-2 items-center justify-center px-3 py-2.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-200/80 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            View All
          </motion.button>
        </div>
        </div>

        {/* Card Footer — fixed at bottom */}
        <div className="mt-auto flex-shrink-0 pt-3 border-t border-gray-100">
          <div className="flex gap-2 items-center text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            <span>
              Created: {new Date(admin.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminCard;
